import { useState } from 'react';
import { Salad } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DietaryPreferencesModal } from '../profile/DietaryPreferencesModal';
import { useAuthStore } from '../../store/useAuthStore';
import { useProfileStore } from '../../store/useProfileStore';
import { hasAnsweredDietaryPrompt } from '../../types/profile';

/**
 * Top-of-feed prompt for users who've never answered the dietary-restrictions
 * question. Tapping it opens the same editor ProfilePage renders as a section.
 *
 * Renders nothing once answered — including when the answer was "none", which
 * is why the gate reads the `dietaryPrefsSet` sentinel and not the tag arrays.
 * Also renders nothing for logged-out visitors (there's no profile to save to)
 * and during the pre-whoami boot window, where `activeProfile` is still null
 * and showing the card would flash it at users who already answered.
 * `activeProfile` is the live profile, not the lighter display snapshot — the
 * dietary fields deliberately aren't in `ProfileSnapshot`.
 */
export const DietaryPromptCard: React.FC = () => {
  const { t } = useTranslation('home');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !activeProfile || hasAnsweredDietaryPrompt(activeProfile)) return null;

  return (
    // Same card shell as the chef prompt / ShareAppCard: text keeps the lion's
    // share of the row, the pill drops to its own full-width line at large text.
    <div className="rounded-2xl border border-border bg-card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2.5">
      <div className="min-w-0 grow-[999] basis-[60%]">
        <p className="text-sm font-medium text-foreground">{t('feed.dietaryPrompt.cardHeading')}</p>
        <p className="text-xs text-muted-foreground">{t('feed.dietaryPrompt.body')}</p>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex grow basis-auto shrink-0 items-center justify-center gap-1.5 rounded-full bg-[var(--ion-color-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.99] transition-transform"
      >
        <Salad className="h-4 w-4" />
        {t('feed.dietaryPrompt.cta')}
      </button>

      <DietaryPreferencesModal isOpen={open} onClose={() => setOpen(false)} />
    </div>
  );
};
